/**
 * Scheduling Service
 * Frontend service for the Calendly-lite scheduling system
 */

import { supabase } from '../config/supabaseClient';
import { ENV } from '../config/env';

const API_BASE = ENV.API_URL;

// ============================================================================
// TYPES
// ============================================================================

export interface UserAvailability {
  id: string;
  organization_id: string;
  user_id: string;
  timezone: string;
  default_meeting_duration: number;
  buffer_before: number;
  buffer_after: number;
  booking_window_days: number;
  min_notice_hours: number;
  is_active: boolean;
}

export interface AvailabilitySlot {
  id: string;
  user_availability_id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
}

export interface AvailabilityOverride {
  id: string;
  user_availability_id: string;
  override_date: string;
  is_available: boolean;
  start_time?: string;
  end_time?: string;
  reason?: string;
}

export interface AvailabilitySettings {
  availability: UserAvailability;
  slots: AvailabilitySlot[];
  overrides: AvailabilityOverride[];
}

export type VideoProvider = 'google_meet' | 'zoom' | 'teams' | 'phone' | 'in_person' | 'none';

export interface IntakeQuestion {
  id: string;
  type: 'text' | 'textarea' | 'select' | 'checkbox' | 'phone' | 'email';
  label: string;
  placeholder?: string;
  required: boolean;
  options?: string[];
}

export interface BookingLink {
  id: string;
  organization_id: string;
  user_id: string;
  slug: string;
  name: string;
  description?: string;
  duration: number;
  video_provider: VideoProvider;
  location_details?: string;
  color: string;
  is_active: boolean;
  requires_confirmation: boolean;
  max_bookings_per_day?: number;
  questions: IntakeQuestion[];
  confirmation_message?: string;
  reminder_hours: number[];
  created_at: string;
  updated_at: string;
}

export interface ScheduledMeeting {
  id: string;
  organization_id: string;
  booking_link_id?: string;
  host_user_id: string;
  start_time: string;
  end_time: string;
  timezone: string;
  booker_name: string;
  booker_email: string;
  booker_phone?: string;
  booker_company?: string;
  video_provider?: VideoProvider;
  video_join_url?: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'no_show';
  source: 'booking_link' | 'ai_telemarketer' | 'manual' | 'import';
  booking_link?: { name: string; color: string };
  created_at: string;
}

export interface TimeSlot {
  start: string;
  end: string;
  available: boolean;
}

export interface UserIntegration {
  id: string;
  provider: string;
  provider_email?: string;
  is_active: boolean;
  connected_at: string;
  last_sync_at?: string;
}

// ============================================================================
// API HELPERS
// ============================================================================

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session?.access_token || ''}`
  };
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: { ...headers, ...options.headers }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `API error: ${response.status}`);
  }

  return response.json();
}

// ============================================================================
// AVAILABILITY
// ============================================================================

export async function getAvailabilitySettings(organizationId: string): Promise<AvailabilitySettings | null> {
  const result = await fetchApi<{ data: AvailabilitySettings | null }>(
    `/api/scheduling/availability?organization_id=${organizationId}`
  );
  return result.data;
}

export async function saveAvailabilitySettings(
  organizationId: string,
  settings: Partial<UserAvailability>,
  slots?: Array<{ day_of_week: number; start_time: string; end_time: string }>
): Promise<AvailabilitySettings> {
  const result = await fetchApi<{ data: AvailabilitySettings }>(
    '/api/scheduling/availability',
    {
      method: 'POST',
      body: JSON.stringify({
        organization_id: organizationId,
        settings,
        slots
      })
    }
  );
  return result.data;
}

export async function createDefaultAvailability(
  organizationId: string,
  timezone: string = 'Europe/London'
): Promise<AvailabilitySettings> {
  const result = await fetchApi<{ data: AvailabilitySettings }>(
    '/api/scheduling/availability/default',
    {
      method: 'POST',
      body: JSON.stringify({ organization_id: organizationId, timezone })
    }
  );
  return result.data;
}

export async function addAvailabilityOverride(
  organizationId: string,
  override: {
    override_date: string;
    is_available: boolean;
    start_time?: string;
    end_time?: string;
    reason?: string;
  }
): Promise<AvailabilityOverride> {
  const result = await fetchApi<{ data: AvailabilityOverride }>(
    '/api/scheduling/availability/override',
    {
      method: 'POST',
      body: JSON.stringify({ organization_id: organizationId, ...override })
    }
  );
  return result.data;
}

export async function deleteAvailabilityOverride(overrideId: string): Promise<void> {
  await fetchApi(`/api/scheduling/availability/override/${overrideId}`, {
    method: 'DELETE'
  });
}

export async function getAvailableSlots(
  organizationId: string,
  date: string,
  duration: number = 30,
  userId?: string
): Promise<TimeSlot[]> {
  const params = new URLSearchParams({
    organization_id: organizationId,
    date,
    duration: String(duration)
  });
  if (userId) params.append('user_id', userId);

  const result = await fetchApi<{ data: TimeSlot[] }>(
    `/api/scheduling/available-slots?${params}`
  );
  return result.data;
}

export async function getAvailableDates(
  organizationId: string,
  duration: number = 30,
  daysAhead: number = 14,
  userId?: string
): Promise<string[]> {
  const params = new URLSearchParams({
    organization_id: organizationId,
    duration: String(duration),
    days_ahead: String(daysAhead)
  });
  if (userId) params.append('user_id', userId);

  const result = await fetchApi<{ data: string[] }>(
    `/api/scheduling/available-dates?${params}`
  );
  return result.data;
}

// ============================================================================
// BOOKING LINKS
// ============================================================================

export async function getBookingLinks(
  organizationId: string,
  all: boolean = false
): Promise<BookingLink[]> {
  const result = await fetchApi<{ data: BookingLink[] }>(
    `/api/scheduling/booking-links?organization_id=${organizationId}&all=${all}`
  );
  return result.data;
}

export async function getBookingLink(id: string): Promise<BookingLink> {
  const result = await fetchApi<{ data: BookingLink }>(
    `/api/scheduling/booking-links/${id}`
  );
  return result.data;
}

export async function createBookingLink(
  organizationId: string,
  data: {
    slug: string;
    name: string;
    description?: string;
    duration?: number;
    video_provider?: VideoProvider;
    color?: string;
    questions?: IntakeQuestion[];
  }
): Promise<BookingLink> {
  const result = await fetchApi<{ data: BookingLink }>(
    '/api/scheduling/booking-links',
    {
      method: 'POST',
      body: JSON.stringify({ organization_id: organizationId, ...data })
    }
  );
  return result.data;
}

export async function updateBookingLink(
  id: string,
  data: Partial<BookingLink>
): Promise<BookingLink> {
  const result = await fetchApi<{ data: BookingLink }>(
    `/api/scheduling/booking-links/${id}`,
    {
      method: 'PUT',
      body: JSON.stringify(data)
    }
  );
  return result.data;
}

export async function deleteBookingLink(id: string): Promise<void> {
  await fetchApi(`/api/scheduling/booking-links/${id}`, {
    method: 'DELETE'
  });
}

// ============================================================================
// PUBLIC BOOKING (No Auth)
// ============================================================================

export async function getPublicBookingPage(slug: string): Promise<{
  booking_link: {
    name: string;
    description?: string;
    duration: number;
    video_provider: VideoProvider;
    questions: IntakeQuestion[];
    color: string;
  };
  available_dates: string[];
  timezone: string;
}> {
  const response = await fetch(`${API_BASE}/api/scheduling/public/booking/${slug}`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Booking link not found');
  }
  const result = await response.json();
  return result.data;
}

export async function getPublicAvailableSlots(
  slug: string,
  date: string
): Promise<TimeSlot[]> {
  const response = await fetch(
    `${API_BASE}/api/scheduling/public/booking/${slug}/slots?date=${date}`
  );
  if (!response.ok) {
    throw new Error('Failed to get available slots');
  }
  const result = await response.json();
  return result.data;
}

export async function bookPublicMeeting(
  slug: string,
  data: {
    start_time: string;
    timezone: string;
    booker_name: string;
    booker_email: string;
    booker_phone?: string;
    booker_company?: string;
    intake_responses?: Record<string, any>;
  }
): Promise<{
  id: string;
  start_time: string;
  end_time: string;
  video_join_url?: string;
  status: string;
}> {
  const response = await fetch(`${API_BASE}/api/scheduling/public/booking/${slug}/book`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to book meeting');
  }
  
  const result = await response.json();
  return result.data;
}

// ============================================================================
// MEETINGS
// ============================================================================

export async function getMeetings(
  organizationId: string,
  options?: {
    all?: boolean;
    status?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
  }
): Promise<ScheduledMeeting[]> {
  const params = new URLSearchParams({ organization_id: organizationId });
  if (options?.all) params.append('all', 'true');
  if (options?.status) params.append('status', options.status);
  if (options?.startDate) params.append('start_date', options.startDate);
  if (options?.endDate) params.append('end_date', options.endDate);
  if (options?.limit) params.append('limit', String(options.limit));

  const result = await fetchApi<{ data: ScheduledMeeting[] }>(
    `/api/scheduling/meetings?${params}`
  );
  return result.data;
}

export async function getMeeting(id: string): Promise<ScheduledMeeting> {
  const result = await fetchApi<{ data: ScheduledMeeting }>(
    `/api/scheduling/meetings/${id}`
  );
  return result.data;
}

export async function createMeeting(
  organizationId: string,
  data: {
    start_time: string;
    end_time: string;
    timezone?: string;
    booker_name: string;
    booker_email: string;
    booker_phone?: string;
    booker_company?: string;
    video_provider?: VideoProvider;
    contact_id?: string;
    company_id?: string;
    deal_id?: string;
    notes?: string;
  }
): Promise<ScheduledMeeting> {
  const result = await fetchApi<{ data: ScheduledMeeting }>(
    '/api/scheduling/meetings',
    {
      method: 'POST',
      body: JSON.stringify({ organization_id: organizationId, ...data })
    }
  );
  return result.data;
}

export async function updateMeetingStatus(
  id: string,
  status: 'confirmed' | 'cancelled' | 'completed' | 'no_show',
  cancellationReason?: string
): Promise<ScheduledMeeting> {
  const result = await fetchApi<{ data: ScheduledMeeting }>(
    `/api/scheduling/meetings/${id}/status`,
    {
      method: 'PUT',
      body: JSON.stringify({ status, cancellation_reason: cancellationReason })
    }
  );
  return result.data;
}

export async function rescheduleMeeting(
  id: string,
  startTime: string,
  endTime: string
): Promise<ScheduledMeeting> {
  const result = await fetchApi<{ data: ScheduledMeeting }>(
    `/api/scheduling/meetings/${id}/reschedule`,
    {
      method: 'PUT',
      body: JSON.stringify({ start_time: startTime, end_time: endTime })
    }
  );
  return result.data;
}

// ============================================================================
// INTEGRATIONS
// ============================================================================

export async function getIntegrations(): Promise<UserIntegration[]> {
  const result = await fetchApi<{ data: UserIntegration[] }>(
    '/api/scheduling/integrations'
  );
  return result.data;
}

export async function getGoogleAuthUrl(): Promise<string> {
  const result = await fetchApi<{ data: { url: string } }>(
    '/api/scheduling/integrations/google/auth-url'
  );
  return result.data.url;
}

export async function getZoomAuthUrl(): Promise<string> {
  const result = await fetchApi<{ data: { url: string } }>(
    '/api/scheduling/integrations/zoom/auth-url'
  );
  return result.data.url;
}

export async function handleGoogleCallback(
  code: string,
  redirectUri: string,
  organizationId: string
): Promise<void> {
  await fetchApi('/api/scheduling/integrations/google/callback', {
    method: 'POST',
    body: JSON.stringify({ code, redirect_uri: redirectUri, organization_id: organizationId })
  });
}

export async function handleZoomCallback(
  code: string,
  redirectUri: string,
  organizationId: string
): Promise<void> {
  await fetchApi('/api/scheduling/integrations/zoom/callback', {
    method: 'POST',
    body: JSON.stringify({ code, redirect_uri: redirectUri, organization_id: organizationId })
  });
}

export async function disconnectIntegration(provider: string): Promise<void> {
  await fetchApi(`/api/scheduling/integrations/${provider}`, {
    method: 'DELETE'
  });
}

// ============================================================================
// AI BOOKING
// ============================================================================

export async function aiBookMeeting(data: {
  organization_id: string;
  host_user_id: string;
  campaign_id?: string;
  call_id?: string;
  prospect_name: string;
  prospect_email: string;
  prospect_phone?: string;
  prospect_company?: string;
  contact_id?: string;
  company_id?: string;
  deal_id?: string;
  preferred_date?: string;
  preferred_time?: string;
  meeting_duration?: number;
  notes?: string;
}): Promise<{
  success: boolean;
  meeting?: ScheduledMeeting;
  available_slots?: Array<{ time: string; datetime: string; readable: string }>;
  error?: string;
}> {
  const result = await fetchApi<{
    data?: ScheduledMeeting;
    success?: boolean;
    available_slots?: any[];
    error?: string;
  }>('/api/scheduling/ai-book', {
    method: 'POST',
    body: JSON.stringify(data)
  });

  if (result.data) {
    return { success: true, meeting: result.data };
  }
  return {
    success: result.success || false,
    available_slots: result.available_slots,
    error: result.error
  };
}

export async function getAIAvailableSlots(
  organizationId: string,
  userId: string,
  date?: string,
  duration: number = 30
): Promise<{
  available: boolean;
  date?: string;
  slots?: Array<{ time: string; datetime: string; readable: string }>;
  next_dates?: string[];
  message?: string;
}> {
  const params = new URLSearchParams({
    organization_id: organizationId,
    user_id: userId,
    duration: String(duration)
  });
  if (date) params.append('date', date);

  const result = await fetchApi<{ data: any }>(
    `/api/scheduling/ai-available-slots?${params}`
  );
  return result.data;
}

export default {
  // Availability
  getAvailabilitySettings,
  saveAvailabilitySettings,
  createDefaultAvailability,
  addAvailabilityOverride,
  deleteAvailabilityOverride,
  getAvailableSlots,
  getAvailableDates,
  
  // Booking Links
  getBookingLinks,
  getBookingLink,
  createBookingLink,
  updateBookingLink,
  deleteBookingLink,
  
  // Public Booking
  getPublicBookingPage,
  getPublicAvailableSlots,
  bookPublicMeeting,
  
  // Meetings
  getMeetings,
  getMeeting,
  createMeeting,
  updateMeetingStatus,
  rescheduleMeeting,
  
  // Integrations
  getIntegrations,
  getGoogleAuthUrl,
  getZoomAuthUrl,
  handleGoogleCallback,
  handleZoomCallback,
  disconnectIntegration,
  
  // AI Booking
  aiBookMeeting,
  getAIAvailableSlots
};
